import React from 'react';
import { Button, Modal, Tooltip } from 'antd';
import {
  CheckOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { MdOutlineOndemandVideo } from 'react-icons/md';
import './must-watch-video.less';
import { VideoType } from '../videoPreview/constants';
import {
  MustWatchVideoNotification,
  MustWatchVideoTranslations,
} from './constants';

// The modal sits above the interaction shield (z-index 1500 in the .less file).
// Tooltips render in their own portal on <body> with antd's default z-index of
// 1070, i.e. behind this modal - they have to be lifted above it explicitly or
// they show up dimmed through the modal instead of on top of it.
const MODAL_Z_INDEX = 2000;
const TOOLTIP_Z_INDEX = MODAL_Z_INDEX + 10;

interface Props {
  open: boolean;
  notification: MustWatchVideoNotification;
  t: MustWatchVideoTranslations;
  isDoneLoading?: boolean;
  /** required + unseen → no close affordance, "Done" is the only way out */
  isBlocking?: boolean;
  /** size of the unseen must-watch queue the arrows navigate (0 = no arrows) */
  queueLength?: number;
  queueIndex?: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  onDone: () => void;
}

const MustWatchVideoModal: React.FC<Props> = ({
  open,
  notification,
  t,
  isDoneLoading,
  isBlocking,
  queueLength = 0,
  queueIndex = -1,
  onPrev,
  onNext,
  onClose,
  onDone,
}) => {
  const hasOwnTitle = !!notification.title;
  const showArrows = queueLength > 1 && queueIndex >= 0;
  const canGoPrev = showArrows && queueIndex > 0;
  const canGoNext = showArrows && queueIndex < queueLength - 1;

  const renderVideo = () => {
    if (notification.videoType === VideoType.MP4 && notification.videoPath) {
      return (
        <video
          key={notification.id}
          controls
          autoPlay
          src={notification.videoPath}
        />
      );
    }
    if (notification.embedSrc) {
      return (
        <iframe
          key={notification.id}
          className="mwv-video-iframe"
          src={notification.embedSrc}
          title={notification.title || t.title}
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      );
    }
    return (
      <div className="mwv-video-fallback">
        <MdOutlineOndemandVideo size={48} />
        <span>{t.videoUnavailable}</span>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      className="must-watch-video-modal"
      // the wrap is flex-centered in the .less file instead of antd's
      // `centered` prop, which uses an inline-block trick that leaves a
      // scrollbar once the modal is a full 100vh tall
      wrapClassName="must-watch-video-modal-wrap"
      closable={!isBlocking}
      maskClosable={false}
      keyboard={false}
      onCancel={onClose}
      width={880}
      title={null}
      footer={null}
      zIndex={MODAL_Z_INDEX}
    >
      <div className="mwv-modal-card">
        <div className="mwv-modal-header">
          <div className="mwv-modal-header-top">
            <span
              className={`mwv-modal-badge ${
                notification.isMustWatch ? '' : 'is-optional'
              }`}
            >
              <span className="mwv-badge-dot" />
              {notification.isMustWatch ? t.badge : t.optionalLabel}
            </span>

            {showArrows && (
              <div className="mwv-modal-nav">
                <Tooltip title={t.prevVideo} zIndex={TOOLTIP_Z_INDEX}>
                  <Button
                    type="text"
                    size="small"
                    icon={<LeftOutlined />}
                    disabled={!canGoPrev}
                    onClick={onPrev}
                  />
                </Tooltip>
                <span className="mwv-modal-counter">
                  {queueIndex + 1} {t.counterOf} {queueLength}
                </span>
                <Tooltip title={t.nextVideo} zIndex={TOOLTIP_Z_INDEX}>
                  <Button
                    type="text"
                    size="small"
                    icon={<RightOutlined />}
                    disabled={!canGoNext}
                    onClick={onNext}
                  />
                </Tooltip>
              </div>
            )}
          </div>

          <h3 className="mwv-modal-heading">
            {hasOwnTitle ? notification.title : t.title}
          </h3>
          {hasOwnTitle && <p className="mwv-modal-subheading">{t.title}</p>}
        </div>

        <div className="mwv-video-stage">
          <div className="mwv-video-wrapper">{renderVideo()}</div>
        </div>

        {!!notification.description && (
          <p className="mwv-modal-description">{notification.description}</p>
        )}

        <div className="mwv-modal-footer">
          {isBlocking ? (
            <span className="mwv-modal-footer-hint">
              <InfoCircleOutlined />
              {t.blockedHint}
            </span>
          ) : (
            <span className="mwv-modal-footer-hint" />
          )}

          {notification.isWatched ? (
            <Button size="large" className="mwv-done-btn" onClick={onClose}>
              {t.closeLabel}
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              className="mwv-done-btn"
              icon={<CheckOutlined />}
              loading={isDoneLoading}
              onClick={onDone}
            >
              {t.done}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default MustWatchVideoModal;
